import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import microbiologyService from '../../../services/microbiologyService';
import './MicrobiologyMasters.css';

// Modules a small/new lab can safely skip configuring on day 1; a mature/full
// lab configures all of these too. "Basic" mode just badges them as optional
// on this hub — nothing is hidden or blocked, so a lab that already configured
// one never loses access.
const ESSENTIAL_MODULE_KEYS = new Set(['organisms', 'antimicrobials', 'panels', 'breakpoints', 'worklist']);

const MICRO_MODULES = [
  {
    key: 'organisms',
    title: 'Organism Master',
    subtitle: 'Taxonomy, binomial nomenclature, Gram types, morphology & synonyms',
    path: '/masters/microbiology/organisms',
    color: '#0284c7',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M9 10h.01M15 10h.01M9.5 15a3.5 3.5 0 0 0 5 0" />
      </svg>
    )
  },
  {
    key: 'antimicrobials',
    title: 'Antimicrobial Master',
    subtitle: 'Antibiotic drug catalog, classes, subclasses, and CLSI Group A/B/C/U tiers',
    path: '/masters/microbiology/antimicrobials',
    color: '#10b981',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
        <path d="m8.5 8.5 7 7" />
      </svg>
    )
  },
  {
    key: 'panels',
    title: 'AST Panel Builder',
    subtitle: 'Dynamic organism-group & specimen drug susceptibility testing panels',
    path: '/masters/microbiology/panels',
    color: '#8b5cf6',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    )
  },
  {
    key: 'breakpoints',
    title: 'Breakpoint Engine Matrix',
    subtitle: 'Versioned clinical breakpoints (EUCAST v16.1 & CLSI M100) for Zone & MIC S/I/R',
    path: '/masters/microbiology/breakpoints',
    color: '#f59e0b',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    )
  },
  {
    key: 'rules',
    title: 'Expert Rules & Resistance Studio',
    subtitle: 'Intrinsic resistance rules, MRSA alerts, ESBL suppression & expected phenotypes',
    path: '/masters/microbiology/rules',
    color: '#ef4444',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    )
  },
  {
    key: 'specimens',
    title: 'Specimens & Culture Methods',
    subtitle: 'Matrix collection protocols, incubation times, temperatures & atmospheres',
    path: '/masters/microbiology/specimens',
    color: '#06b6d4',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2" />
        <path d="M8.5 2h7" />
        <path d="M14.5 16h-5" />
      </svg>
    )
  },
  {
    key: 'worklist',
    title: 'Microbiology Workbench',
    subtitle: 'Accession queue, culture observations, multi-isolate AST grid & validation',
    path: '/microbiology/worklist',
    color: '#2563eb',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    )
  },
  {
    key: 'antibiogram',
    title: 'Antibiogram & AMR Analytics',
    subtitle: 'CLSI M39 cumulative susceptibility rates and epidemiological surveillance',
    path: '/microbiology/antibiogram',
    color: '#d97706',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    )
  },
  {
    key: 'qc',
    title: 'Quality Control, Media & Lots',
    subtitle: 'ATCC control strains, zone/MIC expected ranges, media plates & disc lot tracking',
    path: '/masters/microbiology/qc',
    color: '#059669',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    )
  },
  {
    key: 'barcodes',
    title: 'Barcode & Label Configuration',
    subtitle: 'Symbologies, accession & isolate mapping, check digits, label sizes & thermal printers',
    path: '/masters/microbiology/barcodes',
    color: '#0891b2',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M6 8v8M9 8v5M12 8v8M15 8v3M18 8v8" />
      </svg>
    )
  },
  {
    key: 'retention',
    title: 'Record Retention & Archival Policy',
    subtitle: 'Regulatory lifecycle rules, legal hold locks, warm/cold tiering & disposal authorization',
    path: '/masters/microbiology/retention',
    color: '#475569',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
      </svg>
    )
  }
];

export default function MicrobiologyMastersHub() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMINISTRATOR');

  const [labMode, setLabMode] = useState('Basic');
  const [modeSaving, setModeSaving] = useState(false);

  useEffect(() => {
    microbiologyService.getLabConfig()
      .then((res) => {
        if (res?.data?.labMode) setLabMode(res.data.labMode);
      })
      .catch(() => {
        // Default to Basic on any error; this is a display preference only.
      });
  }, []);

  const handleToggleLabMode = async () => {
    const newMode = labMode === 'Basic' ? 'Full' : 'Basic';
    setModeSaving(true);
    try {
      await microbiologyService.saveLabConfig(newMode);
      setLabMode(newMode);
    } catch (err) {
      console.error('Error saving lab mode:', err);
    } finally {
      setModeSaving(false);
    }
  };

  return (
    <div className="micro-master-container">
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 3a9 9 0 0 1 9 9M12 3a9 9 0 0 0-9 9" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Microbiology Department Architecture & Configuration
          </h1>
          <p>Enterprise-grade masters, clinical standards, dynamic breakpoint engines, and AST panel builders</p>
        </div>
        <div className="micro-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            title={isAdmin ? 'Toggle Basic/Full lab mode (badges optional masters below for small labs)' : 'Only an Administrator can change lab mode'}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px',
              fontWeight: 600, color: '#475569', background: '#f1f5f9',
              border: '1px solid #cbd5e1', borderRadius: '999px', padding: '6px 6px 6px 14px'
            }}
          >
            <span>Lab Mode:</span>
            <button
              type="button"
              onClick={isAdmin ? handleToggleLabMode : undefined}
              disabled={!isAdmin || modeSaving}
              style={{
                border: 'none', borderRadius: '999px', padding: '5px 14px', fontSize: '12.5px',
                fontWeight: 700, cursor: isAdmin ? 'pointer' : 'not-allowed',
                background: labMode === 'Full' ? '#0284c7' : '#64748b', color: '#fff',
                opacity: modeSaving ? 0.6 : 1
              }}
            >
              {modeSaving ? 'Saving...' : labMode === 'Full' ? 'Full Lab' : 'Basic / Small Lab'}
            </button>
          </div>
          <button className="micro-btn-primary" onClick={() => navigate('/microbiology/worklist')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Open Culture Workbench
          </button>
        </div>
      </div>

      <div className="micro-hub-grid">
        {MICRO_MODULES.map((mod) => {
          const isOptional = labMode === 'Basic' && !ESSENTIAL_MODULE_KEYS.has(mod.key);
          return (
            <div key={mod.key} className="micro-card-item" onClick={() => navigate(mod.path)}>
              <div className="micro-card-header">
                <div className="micro-card-icon" style={{ backgroundColor: mod.color }}>
                  {mod.icon}
                </div>
                <div className="micro-card-text">
                  <h3>
                    {mod.title}
                    {isOptional && (
                      <span style={{
                        marginLeft: '8px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.04em', color: '#64748b', background: '#f1f5f9',
                        border: '1px solid #e2e8f0', borderRadius: '999px', padding: '2px 8px', verticalAlign: 'middle'
                      }}>
                        Optional for small labs
                      </span>
                    )}
                  </h3>
                  <p>{mod.subtitle}</p>
                </div>
              </div>
              <div className="micro-card-footer">
                <span>Open Module Configuration</span>
                <span>&rarr;</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
